import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from "docx";
import "../App.css";
import MessageBox from "./MessageBox";
import ConfirmDialog from "./ConfirmDialog";

const HEADERS = [
  "#",
  "(Roll number)Name",
  "PDF Name",
  "Class",
  "Section",
  "Subject",
  "Marks",
  "Comments",
  "Actions",
];

// Fixed column widths in percentages for each header (9 columns including row number and actions)
const COL_WIDTHS = [2, 12, 10, 5, 5, 8, 6, 43, 9];

export default function TablesTab({ data, onClear, onUpdateData }) {
  const [tableData, setTableData] = useState(data || []);
  const [sortConfig, setSortConfig] = useState({ column: null, direction: 'asc' });
  const [approveForm, setApproveForm] = useState({
    class: '',
    section: '',
    subject: ''
  });
  const [showApproveForm, setShowApproveForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [studentLists, setStudentLists] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [messageBox, setMessageBox] = useState({ show: false, text: '', type: 'info' });
  const [confirm, setConfirm] = useState({ show: false, text: '', onConfirm: null });

  useEffect(() => {
    setTableData(data || []);
  }, [data]);

  useEffect(() => {
    fetchStudentLists();
  }, []);

  async function fetchStudentLists() {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${window.API_BASE_URL}/students/lists`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Apply natural sort to student lists
        const sortedLists = (data || []).sort((a, b) => {
          // Create sortable strings from class-section subject
          const titleA = `${a.class || ''}-${a.section || ''} ${a.subject || ''}`;
          const titleB = `${b.class || ''}-${b.section || ''} ${b.subject || ''}`;

          // Use localeCompare with numeric option for natural sorting
          return titleA.localeCompare(titleB, undefined, { 
            numeric: true, 
            sensitivity: 'base' 
          });
        });
        setStudentLists(sortedLists);
      }
    } catch (err) {
      console.error('Error fetching student lists:', err);
    }
  }

  function deleteRow(index) {
    const updated = tableData.filter((_, i) => i !== index);
    setTableData(updated);
    if (onUpdateData) {
      onUpdateData(updated);
    }
  }

  function handleCellChange(rowIndex, colIndex, value) {
    setTableData((prev) => {
      const updated = prev.map((row, rIdx) =>
        rIdx === rowIndex
          ? row.map((cell, cIdx) => (cIdx === colIndex ? value : cell))
          : row
      );
      if (onUpdateData) {
        onUpdateData(updated);
      }
      return updated;
    });
  }

  function handleSort(columnIndex) {
    let direction = 'asc';
    if (sortConfig.column === columnIndex && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    const sorted = [...tableData].sort((a, b) => {
      let aVal = a[columnIndex] || '';
      let bVal = b[columnIndex] || '';

      // Special handling for Marks column (index 5) - sort numerically
      if (columnIndex === 5) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // String comparison for other columns
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();

      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setTableData(sorted);
    setSortConfig({ column: columnIndex, direction });
    if (onUpdateData) {
      onUpdateData(sorted);
    }
  }

  async function handleApprove() {
    setLoading(true);
    setMessage({ text: '', type: '' });
    
    try {
      const { class: className, section, subject } = approveForm;
      
      if (!className || !section || !subject) {
        setMessage({ text: 'Please fill in all fields', type: 'error' });
        setLoading(false);
        return;
      }
      
      if (tableData.length === 0) {
        setMessage({ text: 'No checked data to approve', type: 'error' });
        setLoading(false);
        return;
      }

      // Declare token early so the clearing block can use it
      const token = localStorage.getItem('authToken');

      // Clear ALL existing data in the selected student list before submitting new data
      try {
        const studentsRes = await fetch(
          `${window.API_BASE_URL}/students?class=${encodeURIComponent(className)}&section=${encodeURIComponent(section)}&subject=${encodeURIComponent(subject)}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (studentsRes.ok) {
          const existingStudents = await studentsRes.json();
          if (existingStudents.length > 0) {
            await Promise.all(
              existingStudents.map(student =>
                fetch(`${window.API_BASE_URL}/students/${student.id}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                  body: JSON.stringify({ student_name: student.student_name, first_name: '', marks: '', comments: '' })
                })
                .then(r => { if (!r.ok) console.error(`Failed to clear student ${student.id}: ${r.status}`); })
                .catch(e => console.error(`Error clearing student ${student.id}:`, e))
              )
            );
          }
        } else {
          console.error('Failed to fetch students for clearing:', studentsRes.status);
        }
      } catch (clearErr) {
        console.error('Error clearing existing student data:', clearErr);
        // Continue with approve even if clearing fails
      }

      // Prepare checked data with student list name appended to comments
      const listName = `${className} - ${section} - ${subject}`;
      const checkedData = tableData.map(row => ({
        name: row[0],      // (Roll number)Name
        pdf_name: row[1],  // PDF Name
        marks: row[5],     // Marks
        comments: row[6] ? `${row[6]} | ${listName}` : `${listName}`   // Comments with list name appended
      }));
      
      const response = await fetch(`${window.API_BASE_URL}/students/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          class: className,
          section,
          subject,
          checkedData
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        
        let message = result.message;
        if (result.unmatched && result.unmatched.length > 0) {
          message += `. ${result.unmatched.length} unmatched - saved to Mistakes tab`;
        }
        
        setMessage({ text: message, type: 'success' });
        setShowApproveForm(false);
        // Save the approved class-subject label for display in Students tab
        localStorage.setItem('lastApprovedAssessmentLabel', `${className}-${section} ${subject}`.trim());
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to approve');
      }
    } catch (err) {

      setMessage({ text: err.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  async function downloadXlsm() {
    const wsData = [
      [
        "(Roll number)Name",
        "PDF Name",
        "Class",
        "Section",
        "Subject",
        "Marks",
        "Comments",
      ],
      ...tableData,
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const wbout = XLSX.write(wb, { bookType: "xlsm", type: "array" });
    
    const blob = new Blob([wbout], {
      type: "application/vnd.ms-excel.sheet.macroEnabled.12",
    });

    try {
      // Check if File System Access API is supported
      if ('showSaveFilePicker' in window) {
        const handle = await window.showSaveFilePicker({
          suggestedName: 'RESULT.xlsm',
          types: [{
            description: 'Excel Macro-Enabled Workbook',
            accept: { 'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xlsm'] },
          }],
          // Start in the desired directory (browser will remember last used directory)
          startIn: 'documents',
        });
        
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();

        setMessageBox({ show: true, text: 'File saved successfully!', type: 'success' });
      } else {
        // Fallback for browsers that don't support File System Access API
        saveAs(blob, "RESULT.xlsm");
      }
    } catch (err) {
      // User cancelled or error occurred
      if (err.name !== 'AbortError') {

        // Fallback to regular download
        saveAs(blob, "RESULT.xlsm");
      } else {
        return; // User cancelled, don't show the clear prompt
      }
    }

    // 🔥 Confirmation after download
    showConfirm("Excel extracted successfully.\nDo you want to clear the table?", () => {
      setTableData([]);
      if (onClear) onClear();
    });
  }

  const showConfirm = (text, callback) => setConfirm({ show: true, text, onConfirm: callback });
  const handleConfirm = () => { 
    confirm.onConfirm(); 
    setConfirm({ show: false, text: '', onConfirm: null }); 
  };
  const handleCancel = () => setConfirm({ show: false, text: '', onConfirm: null });

  async function printComments() {
    if (tableData.length === 0) {
      setMessageBox({ show: true, text: 'No data to print!', type: 'error' });
      return;
    }

    try {
      // Create document sections for each student
      const sections = [];

      tableData.forEach((row, index) => {
        const studentName = row[0] || 'Unknown';
        const comments = row[6] || 'No comments';

        // Add student number and name as heading
        sections.push(
          new Paragraph({
            text: `${index + 1}. ${studentName}`,
            heading: HeadingLevel.HEADING_2,
            spacing: {
              before: index === 0 ? 0 : 400,
              after: 200
            }
          })
        );

        // Add comments
        sections.push(
          new Paragraph({
            children: [
              new TextRun({
                text: comments,
                size: 24 // 12pt font
              })
            ],
            spacing: {
              after: 200
            }
          })
        );
      });

      // Create the document
      const doc = new Document({
        sections: [{
          properties: {},
          children: [
            new Paragraph({
              text: "Student Comments",
              heading: HeadingLevel.HEADING_1,
              spacing: {
                after: 400
              }
            }),
            ...sections
          ]
        }]
      });

      // Generate and save the document
      const blob = await Packer.toBlob(doc);
      saveAs(blob, "Student_Comments.docx");
      
      setMessageBox({ show: true, text: 'Word document downloaded successfully!', type: 'success' });
    } catch (err) {
      console.error('Error generating Word document:', err);
      setMessageBox({ show: true, text: 'Failed to generate Word document: ' + err.message, type: 'error' });
    }
  }

  return (
    <div className="tables-tab" style={{ padding: '24px', maxWidth: 1600, margin: '0 auto', background: 'var(--surface, #ffffff)' }}>
      {/* Header Section */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #EC4899 0%, #DB2777 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(236, 72, 153, 0.3)'
            }}>
              <i className="fas fa-table" style={{ color: 'white', fontSize: '24px' }}></i>
            </div>
            <div>
              <h1 style={{ 
                color: 'var(--text-primary)', 
                margin: 0,
                fontSize: '1.35rem',
                fontWeight: '700'
              }}>Results Table</h1>
              <p style={{
                margin: 0,
                color: 'var(--text-muted)',
                fontSize: '0.85rem'
              }}>View and manage grading results • {tableData.length} student(s)</p>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button 
              onClick={() => setShowApproveForm(!showApproveForm)}
              style={{
                padding: '12px 20px',
                fontSize: '0.95rem',
                fontWeight: '600',
                borderRadius: '10px',
                border: 'none',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
              }}
              title="Approve and submit results"
            >
              <i className="fa-solid fa-check"></i>
              Approve
            </button>
            <button 
              onClick={printComments}
              style={{
                padding: '12px 16px',
                fontSize: '0.95rem',
                fontWeight: '600',
                borderRadius: '10px',
                border: 'none',
                background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.3)';
              }}
              title="Print comments to Word"
            >
              <i className="fa-solid fa-print"></i>
            </button>
            <button 
              onClick={downloadXlsm}
              style={{
                padding: '12px 16px',
                fontSize: '0.95rem',
                fontWeight: '600',
                borderRadius: '10px',
                border: 'none',
                background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(139, 92, 246, 0.3)';
              }}
              title="Download Excel file"
            >
              <i className="fa-solid fa-download"></i>
            </button>
            <button
              onClick={() => {
                showConfirm('Are you sure you want to clear all data?', () => {
                  onClear();
                  setTableData([]);
                });
              }}
              style={{
                padding: '12px 16px',
                fontSize: '0.95rem',
                fontWeight: '600',
                borderRadius: '10px',
                border: 'none',
                background: 'linear-gradient(135deg, #EF4444, #DC2626)',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(239, 68, 68, 0.3)';
              }}
              title="Clear all data"
            >
              <i className="fa-solid fa-trash"></i>
            </button>
          </div>
        </div>
      </div>
      
      {/* Approve Form */}
      {showApproveForm && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(5, 150, 105, 0.05))',
          borderLeft: '4px solid #10b981',
          padding: '20px',
          borderRadius: '12px',
          marginBottom: '24px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #10b981, #059669)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white'
            }}>
              <i className="fas fa-check-circle"></i>
            </div>
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: 'var(--text-primary)' }}>Approve Checked Data</h3>
          </div>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <select
              value={`${approveForm.class}|${approveForm.section}|${approveForm.subject}`}
              onChange={(e) => {
                const [cls, sec, sub] = e.target.value.split('|');
                setApproveForm({ class: cls, section: sec, subject: sub });
              }}
              style={{ 
                flex: 1, 
                minWidth: '250px',
                padding: '12px 16px', 
                borderRadius: '8px', 
                border: '1px solid var(--border)',
                fontSize: '0.95rem',
                background: 'var(--surface)',
                color: 'var(--text-primary)'
              }}
            >
              <option value="||">Select Student List</option>
              {studentLists.map((list, idx) => (
                <option 
                  key={idx} 
                  value={`${list.class}|${list.section}|${list.subject}`}
                >
                  {list.class}-{list.section} {list.subject} ({list.student_count} students)
                </option>
              ))}
            </select>
            <button 
              onClick={handleApprove}
              disabled={loading}
              style={{
                padding: '12px 24px',
                fontSize: '0.95rem',
                fontWeight: '600',
                borderRadius: '8px',
                border: 'none',
                background: loading ? '#94a3b8' : 'linear-gradient(135deg, #10b981, #059669)',
                color: 'white',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: loading ? 'none' : '0 2px 8px rgba(16, 185, 129, 0.3)',
                transition: 'all 0.3s ease',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Approving...
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane"></i>
                  Submit Approval
                </>
              )}
            </button>
          </div>
        </div>
      )}
      
      {/* Messages */}
      {message.text && (
        <div style={{
          padding: '14px 18px',
          borderRadius: '10px',
          marginBottom: '20px',
          background: message.type === 'success' 
            ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(5, 150, 105, 0.1))' 
            : 'linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(220, 38, 38, 0.1))',
          color: message.type === 'success' ? '#047857' : '#991b1b',
          border: `1px solid ${message.type === 'success' ? '#10b981' : '#EF4444'}`,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          fontSize: '0.95rem',
          fontWeight: '500'
        }}>
          <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`} style={{ fontSize: '1.25rem' }}></i>
          {message.text}
        </div>
      )}

      <div style={{
        background: 'var(--surface)',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
        border: '1px solid var(--border)'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ 
            tableLayout: 'fixed', 
            width: '100%',
            borderCollapse: 'collapse'
          }}>
            <thead>
              <tr>
                {HEADERS.map((h, idx) => (
                  <th
                    key={h}
                    onClick={() => {
                      // Don't sort on # column (0) or Delete column (8)
                      if (idx !== 0 && idx !== 8) {
                        handleSort(idx - 1); // Subtract 1 because data columns start at index 0
                      }
                    }}
                    style={{
                      position: "relative",
                      borderBottom: "1px solid #ddd",
                      textAlign: "left",
                      whiteSpace: idx === 7 ? "normal" : "nowrap",
                      width: `${COL_WIDTHS[idx]}%`,
                      minWidth: `${COL_WIDTHS[idx]}%`,
                      cursor: (idx !== 0 && idx !== 8) ? "pointer" : "default",
                      userSelect: "none",
                    }}
                  >
                    {h}
                    {idx !== 0 && idx !== 8 && (
                      <span style={{ marginLeft: 6, fontSize: 12, opacity: 0.6 }}>
                        {sortConfig.column === idx - 1 ? (
                          sortConfig.direction === 'asc' ? '▲' : '▼'
                        ) : '⇅'}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, idx) => (
                <tr key={idx}>
                  <td
                    style={{
                      padding: 2,
                      borderBottom: "1px solid #f2f2f2",
                      verticalAlign: "top",
                      textAlign: "center",
                      fontWeight: "bold",
                      color: "#666",
                      width: `${COL_WIDTHS[0]}%`,
                      minWidth: `${COL_WIDTHS[0]}%`,
                    }}
                  >
                    {idx + 1}
                  </td>
                  {row.map((cell, cidx) => (
                    <td
                      key={cidx}
                      style={{
                        padding: 2,
                        borderBottom: "1px solid #f2f2f2",
                        verticalAlign: "top",
                        whiteSpace: cidx === 6 ? "normal" : "nowrap",
                        wordWrap: cidx === 6 ? "break-word" : "normal",
                        overflow: "hidden",
                        width: `${COL_WIDTHS[cidx + 1]}%`,
                        minWidth: `${COL_WIDTHS[cidx + 1]}%`,
                      }}
                    >
                      {cidx === 6 ? (
                        <div style={{
                          maxHeight: expandedRows.has(idx) ? "none" : "1.5em",
                          overflow: "hidden",
                          textOverflow: expandedRows.has(idx) ? "clip" : "ellipsis",
                          whiteSpace: expandedRows.has(idx) ? "pre-wrap" : "nowrap",
                          wordWrap: "break-word"
                        }}>
                          {cell}
                        </div>
                      ) : (
                        <input
                          type="text"
                          value={cell}
                          onChange={(e) =>
                            handleCellChange(idx, cidx, e.target.value)
                          }
                          style={{
                            width: "100%",
                            border: "none",
                            background: "transparent",
                            outline: "none",
                            font: "inherit",
                            whiteSpace: "nowrap",
                          }}
                        />
                      )}
                    </td>
                  ))}
                  <td style={{ textAlign: "center", whiteSpace: "nowrap", padding: "4px" }}>
                    <div style={{ display: "flex", gap: "4px", justifyContent: "center" }}>
                      {expandedRows.has(idx) ? (
                        <button
                          onClick={() => {
                            const newExpanded = new Set(expandedRows);
                            newExpanded.delete(idx);
                            setExpandedRows(newExpanded);
                          }}
                          style={{
                            padding: "4px 8px",
                            fontSize: "12px",
                            border: "none",
                            borderRadius: "4px",
                            background: "#FF9800",
                            color: "white",
                            cursor: "pointer"
                          }}
                          title="Hide comment"
                        >
                          <i className="fa-solid fa-eye-slash"></i>
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            const newExpanded = new Set(expandedRows);
                            newExpanded.add(idx);
                            setExpandedRows(newExpanded);
                          }}
                          style={{
                            padding: "4px 8px",
                            fontSize: "12px",
                            border: "none",
                            borderRadius: "4px",
                            background: "#2196F3",
                            color: "white",
                            cursor: "pointer"
                          }}
                          title="Show full comment"
                        >
                          <i className="fa-solid fa-eye"></i>
                        </button>
                      )}
                      <button
                        className="trash-btn"
                        onClick={() => deleteRow(idx)}
                        style={{
                          padding: "4px 8px",
                          fontSize: "12px"
                        }}
                        title="Delete row"
                      >
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      <MessageBox 
        message={messageBox} 
        onClose={() => setMessageBox({ show: false, text: '', type: 'info' })} 
      />
      
      <ConfirmDialog 
        confirm={confirm} 
        onConfirm={handleConfirm} 
        onCancel={handleCancel} 
      />
    </div>
  );
}
